import React, { Children, useCallback, useEffect, useRef, useState } from "react";
import styles from "./cardStack.module.css";

const SWIPE_DISTANCE = 0.3; // fraction of stack width
const SWIPE_VELOCITY = 0.5; // px per ms
const EXIT_MS = 320; // matches --duration-slow
const FAN = [
  { rotate: 0, x: 0, scale: 1 },
  { rotate: 3, x: 8, scale: 0.97 },
  { rotate: 6, x: 16, scale: 0.94 },
];

/**
 * Swipeable, looping stack of cards. Swipe left or right to send the top card to the back.
 *
 * <CardStack label="Testimonials">
 *   {items.map((item) => <Card key={item.id} {...item} />)}
 * </CardStack>
 */
function CardStack({ children, label = "Card stack" }) {
  const cards = Children.toArray(children);
  const count = cards.length;
  const stackRef = useRef(null);
  const dragRef = useRef(null);
  const timerRef = useRef(null);
  const [top, setTop] = useState(0);
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [exitDir, setExitDir] = useState(0);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const fling = useCallback(
    (dir) => {
      if (exitDir || count < 2) return;
      setExitDir(dir);
      timerRef.current = setTimeout(() => {
        setTop((t) => (t + 1) % count);
        setExitDir(0);
        setDx(0);
      }, EXIT_MS);
    },
    [exitDir, count]
  );

  const handlePointerDown = (e) => {
    if (exitDir || count < 2 || e.button !== 0) return;
    dragRef.current = { x: e.clientX, t: performance.now() };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current) return;
    setDx(e.clientX - dragRef.current.x);
  };

  const handlePointerUp = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    setDragging(false);

    const delta = e.clientX - drag.x;
    const velocity = Math.abs(delta) / Math.max(performance.now() - drag.t, 1);
    const width = stackRef.current?.offsetWidth || 1;

    if (Math.abs(delta) > width * SWIPE_DISTANCE || (velocity > SWIPE_VELOCITY && Math.abs(delta) > 20)) {
      fling(Math.sign(delta));
    } else {
      setDx(0);
    }
  };

  const handlePointerCancel = () => {
    dragRef.current = null;
    setDragging(false);
    setDx(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowRight") fling(1);
    else if (e.key === "ArrowLeft") fling(-1);
  };

  if (!count) return null;

  return (
    <div
      className={styles.stack}
      ref={stackRef}
      onKeyDown={handleKeyDown}
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      tabIndex={0}
    >
      {cards.map((card, i) => {
        const depth = (i - top + count) % count;
        const isTop = depth === 0;
        let transform;
        let opacity = 1;

        if (isTop) {
          transform = exitDir
            ? `translateX(${exitDir * 150}%) rotate(${exitDir * 18}deg)`
            : `translateX(${dx}px) rotate(${dx * 0.05}deg)`;
        } else {
          const fan = FAN[Math.min(depth, FAN.length - 1)];
          transform = `translateX(${fan.x}px) rotate(${fan.rotate}deg) scale(${fan.scale})`;
          if (depth >= FAN.length) opacity = 0;
        }

        return (
          <div
            key={card.key ?? i}
            className={`${styles.card} ${isTop ? styles.top : ""} ${isTop && dragging ? styles.dragging : ""}`}
            style={{ transform, opacity, zIndex: count - depth }}
            aria-hidden={!isTop}
            {...(isTop && {
              onPointerDown: handlePointerDown,
              onPointerMove: handlePointerMove,
              onPointerUp: handlePointerUp,
              onPointerCancel: handlePointerCancel,
            })}
          >
            {card}
          </div>
        );
      })}
    </div>
  );
}

export default CardStack;
